import * as z from 'zod'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import calendar from 'dayjs/plugin/calendar.js'
import duration from 'dayjs/plugin/duration.js'
import weekday from 'dayjs/plugin/weekday.js'
import localeData from 'dayjs/plugin/localeData.js'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js'
import weekOfYear from 'dayjs/plugin/weekOfYear.js'

import esp from 'dayjs/locale/es-mx.js'
import eng from 'dayjs/locale/en.js'
import por from 'dayjs/locale/pt-br.js'
import minMax from 'dayjs/plugin/minMax.js'

import utc from 'dayjs/plugin/utc.js'
import timezone from 'dayjs/plugin/timezone.js'
import isoWeek from 'dayjs/plugin/isoWeek.js'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import isBetween from 'dayjs/plugin/isBetween.js'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import objectSupport from 'dayjs/plugin/objectSupport.js'
import LocalizedFormat from 'dayjs/plugin/localizedFormat.js'

import { idiomaVisible } from '../lib/rosettas'
import type { IdiomasDisponibles } from '../lib/rosettas'
import { ErrorAumentado } from '../lib/error'
import { NumeroOpcionalZod } from '@comun/zodComun'

export type { Dayjs } from 'dayjs'
export type { Duration, DurationUnitsObjectType, DurationUnitType } from 'dayjs/plugin/duration.js'



dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isoWeek)
dayjs.extend(relativeTime)
dayjs.extend(isBetween)
dayjs.extend(customParseFormat)
dayjs.extend(objectSupport)
dayjs.extend(LocalizedFormat)
dayjs.extend(duration)

dayjs.extend(minMax)
dayjs.extend(calendar)
dayjs.extend(duration)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(weekOfYear)
dayjs.locale('es-mx')

declare module 'dayjs' {
  function range(start: Dayjs, end: Dayjs): Dayjs[]
  function mostrarRangoFechas(inicio: Date, fin: Date, timezone: string): string
  function mostrarFecha(fecha: Dayjs): string
  function mostrarDiaEnContexto(fecha: Dayjs): string
  function calendario(): string
  function fechaContextual(fecha: Dayjs): string
  function fechayHoraContextual(fecha: Dayjs): string
  function cambiarIdiomaAlDeRosetta(idiomaVisible: IdiomasDisponibles): void
}


dayjs.range = (start: Dayjs, end: Dayjs): Dayjs[] => {
  const range = []
  for (let i = start; i <= end; i = i.add(1, 'day'))
    range.push(i)

  return range
}

// Preprocesador para convertir varios tipos a Date o undefined
function preprocesadorFecha(v: unknown): Date | undefined { // Añadimos ctx opcional
  // Si el valor es undefined o null, retornamos undefined directamente
  // const fx = 'preprocesadorFecha'
  const rutaDelCampo = 'desconocida'
  if (v === undefined || v === null) {
    return undefined;
  }

  const error = new Error('Dayjs componentes resultaron en Date inválido para ${fx}');

  // Si el valor es un objeto Dayjs
  if (dayjs.isDayjs(v)) {
    const internalDate = (_.isObject(v) && '$d' in v) ? v.$d : v;

    if (internalDate instanceof Date && !Number.isNaN(internalDate.getTime())) {
      return new Date(internalDate);
    } else {
      consolo.warn('${fx}: El objeto $d interno de Dayjs no es un Date válido o no existe.', { path: rutaDelCampo, value: v });
      consolo.log('${fx}: $d no válido. Intentando extraer componentes de Dayjs.', { path: rutaDelCampo });
      try {
        const year = v.year();
        const month = v.month();
        const day = v.date();
        const hour = v.hour();
        const minute = v.minute();
        const second = v.second();
        const millisecond = v.millisecond();

        const dateFromComponents = new Date(year, month, day, hour, minute, second, millisecond);

        if (!Number.isNaN(dateFromComponents.getTime())) {
          return dateFromComponents;
        } else {
          // --- MODIFICACIÓN AQUÍ: Stack para Dayjs -> componentes -> Date inválido ---
          if (rutaDelCampo) consolo.warn(
            '${fx}: Creado Date inválido a partir de componentes Dayjs.',
            {
              resultadoDelIntento: dateFromComponents, // Mostrar el objeto Date inválido
              rutaDelCampo,
              stackDeLlamada: error.stack
            }
          );
          // --- Fin de la modificación ---
          return undefined;
        }
      } catch (e) {
        consolo.error('${fx}: Error durante extracción de componentes de Dayjs:', e, { path: rutaDelCampo });
        return undefined;
      }
    }
  }
  // Si el valor ya es un objeto Date nativo
  else if (v instanceof Date) {
    // Verificamos si es una fecha válida
    if (!Number.isNaN(v.getTime())) {
      return new Date(v);
    } else {
      // --- MODIFICACIÓN AQUÍ: Stack para Input Date nativo inválido ---
      // const error = new Error('Input Date nativo inválido recibido por ${fx}');
      // if (rutaDelCampo) consolo.warn(
      //     '${fx}: El input Date nativo es inválido.',
      //     {
      //         valorDelInput: v, // Mostrar el objeto Date inválido
      //         rutaDelCampo,
      //         stackDeLlamada: error.stack
      //     }
      // );
      // --- Fin de la modificación ---
      return undefined;
    }
  }
  // Si el valor es un string
  else if (typeof v === 'string') {
    const date = new Date(v);
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return new Date(date);
    }
    // Si es un string pero no se puede parsear, mostramos una advertencia
    // --- MODIFICACIÓN AQUÍ: Stack para Input string no parseable ---
    const error = new Error('Input string no parseable a Date recibido por ${fx}');
    consolo.warn(
      '${fx}, es string pero no logra ser parseado',
      {
        tipoDeInput: typeof v,
        valorDelInput: v,
        rutaDelCampo,
        stackDeLlamada: error.stack
      }
    );
    // --- Fin de la modificación ---
    return undefined;
  }
  // Si el valor no es de ninguno de los tipos esperados
  else {
    // Crear un objeto Error para capturar el stack trace
    const error = new Error('Input de tipo desconocido recibido por ${fx}');

    consolo.warn(
      '${fx}: El input es de un tipo desconocido.',
      {
        tipoDeInput: typeof v,
        valorDelInput: v,
        rutaDelCampo,
        stackDeLlamada: error.stack
      }
    );
    return undefined;
  }
}


function preprocesadorFechaYMD(v: unknown): string | undefined {
  if (typeof v !== 'string')
    return preprocesadorFecha(v)?.toISOString().split('T')[0]
  const validable = dayjs(v, 'YYYY-MM-DD')
  return validable.isValid() ? v : undefined
}

export const FechaZod = z.preprocess(
  preprocesadorFecha,
  z.date()
);

export const FechaYMDZod = z.preprocess(preprocesadorFechaYMD, z.string())
export const FechaYMDOpcional = z.preprocess(preprocesadorFechaYMD, z.string().optional())

export const FechaOpcionalZod = z.preprocess(v => preprocesadorFecha(v), z.date().optional()).optional()
export const FechaONullZod = z.preprocess(v => preprocesadorFecha(v) || null, z.date().nullable())
export const FechaOAhoraZod = z.preprocess(v => preprocesadorFecha(v) || new Date(), z.date())

function PreprocesadorDayjs(v: unknown) {
  if (dayjs.isDayjs(v))
    return v
  if (typeof v === 'string')
    return dayjs(v)
  if (typeof v === 'object')
    return dayjs(v)
  return null
}

export const FechaADayjsZod = z
  .preprocess(PreprocesadorDayjs, z.custom<Dayjs>())

function preprocesadorFechaDMY(v: unknown): Date | undefined {
  if (v === undefined || v === null)
    return undefined
  if (typeof v !== 'string')
    return undefined
  return dayjs(v, 'DD/MM/YYYY').toDate()
}
export const DMYaFechaZod = z.preprocess(preprocesadorFechaDMY, z.date())
export const FechaDMYAISOZod = DMYaFechaZod.transform(v => dayjs(v).toISOString())

export const FechaADayjsOpcionalZod = z
  .preprocess(v => preprocesadorFecha(v) || new Date(), z.date()).optional()
  .transform(v => v ? dayjs(v) : undefined)

export const FechaAISOZod = z
  .preprocess(v => preprocesadorFecha(v) || new Date(), z.date())
  .transform(v => dayjs(v).toISOString())

export const FechaAYMDZod = z
  .preprocess(v => preprocesadorFecha(v) || new Date(), z.date())
  .transform(v => dayjs(v).format('YYYY-MM-DD'))

export function EsFecha(puedeSer: unknown) {
  const revision = FechaZod.safeParse(puedeSer)
  return revision.success
}

const horaRegex = /^(0\d|1\d|2[0-3]):[0-5]\d$/
export const HoraZod = z.string().trim().regex(horaRegex)
export const DiaSemanaZod = z.number().min(1).max(7)

export const DuracionZod = z.object({
  years: NumeroOpcionalZod,
  months: NumeroOpcionalZod,
  days: NumeroOpcionalZod,
  hours: NumeroOpcionalZod,
  minutes: NumeroOpcionalZod,
  seconds: NumeroOpcionalZod,
  milliseconds: NumeroOpcionalZod
})

export type Duracion = z.infer<typeof DuracionZod>


dayjs.mostrarRangoFechas = (inicio: Date, fin: Date, timezone: string): string => {
  const elInicio = dayjs(inicio).tz(timezone)
  const elFin = dayjs(fin).tz(timezone)
  if (!elInicio || !elFin)
    throw new ErrorAumentado('Fail mostrarRangoFechas, falta inicio o fin', { datos: { inicio: elInicio, fin: elFin } })
  const ahora = dayjs()

  if (elInicio.isSame(elFin, 'day'))
    return `${elInicio.format('D MMM.')}`

  if (elInicio.isSame(elFin, 'month')) {
    if (ahora.isSame(elFin, 'year'))
      return `${elInicio.format('D')} - ${elFin.format('D MMM.')}`

    return `${elInicio.format('D')} - ${elFin.format('D MMM. YYYY')}`
  }
  if (elInicio.isSame(elFin, 'year')) {
    if (ahora.isSame(elFin, 'year'))
      return `${elInicio.format('D MMM.')} - ${elFin.format('D MMM.')}`

    return `${elInicio.format('D MMM.')} - ${elFin.format('D MMM. YYYY')}`
  }

  return `${elInicio.isSame(ahora, 'year')
    ? elInicio.format('D MMM.')
    : elInicio.format('D MMM. YYYY')
  } - ${elFin.isSame(ahora, 'year') ? elFin.format('D MMM') : elFin.format('D MMM. YYYY')
  }`
}

dayjs.mostrarFecha = (fecha: Dayjs): string => {
  const hoy = dayjs()

  if (fecha.isSame(hoy, 'day'))
    return fecha.format('H:mm a')

  if (fecha.isSame(hoy, 'month'))
    return fecha.format('dddd D, H:mm a')

  if (fecha.isSame(hoy, 'year'))
    return fecha.format('dddd D MMM., H:mm a')

  return fecha.format('dddd D MMM. YYYY, H:mm a')
}


dayjs.mostrarDiaEnContexto = (fecha: Dayjs): string => {
  const contextoEN = {
    sameDay: '[today]',
    nextDay: '[tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[yesterday]',
    lastWeek: '[last] dddd',
    sameElse: 'dddd D'
  }
  const contextoES = {
    sameDay: '[hoy]',
    nextDay: '[mañana]',
    nextWeek: '[próximo] dddd',
    lastDay: '[ayer]',
    lastWeek: 'dddd [pasado]',
    sameElse: 'dddd D'
  }

  const contextoPT = {
    sameDay: '[hoje]',
    nextDay: '[amanhã]',
    nextWeek: 'dddd',
    lastDay: '[ontem]',
    lastWeek: '[último] dddd',
    sameElse: 'dddd D'
  }

  const idiomaID: IdiomasDisponibles = unref(idiomaVisible) || 'es'
  return fecha.calendar(
    null,
    {
      es: contextoES,
      en: contextoEN,
      pt: contextoPT
    }[idiomaID]
  )
}

export const localeActivo = computed(() => {
  const idiomaID = unref(idiomaVisible)
  if (idiomaID === 'en')
    return 'en'
  if (idiomaID === 'pt')
    return 'pt-br'
  else return 'es-mx'
})

function cambiarIdiomaAlDeRosetta(idiomaVisible: IdiomasDisponibles) {
  switch (idiomaVisible) {
    case 'en':
      dayjs.locale(eng)
      break
    case 'pt':
      dayjs.locale(por)
      break
    default:
      dayjs.locale(esp)
  }
}

watch(idiomaVisible, (idiomaID: IdiomasDisponibles) => {
  if (!idiomaID)
    return
  cambiarIdiomaAlDeRosetta(idiomaID)
}, { immediate: true })

export function fechaContextual(fecha: Dayjs) {
  const contextoEN = {
    sameDay: '[Today at] h:mm a',
    nextDay: '[Tomorrow at] h:mm a',
    nextWeek: 'dddd [at] h:mm a',
    lastDay: '[Yesterday at] h:mm a',
    lastWeek: '[Last] dddd [at] h:mm a',
    sameElse: 'dddd D MMM. YYYY'
  }
  const contextoES = {
    sameDay: '[Hoy a las] h:mm a',
    nextDay: '[Mañana a las] h:mm a',
    nextWeek: 'dddd [a las] h:mm a',
    lastDay: '[Ayer a las] h:mm a',
    lastWeek: '[El pasado] dddd [a las] h:mm a',
    sameElse: 'dddd D MMM. YYYY'
  }

  const contextoPT = {
    sameDay: '[Hoje às] h:mm a',
    nextDay: '[Amanhã às] h:mm a',
    nextWeek: 'dddd [às] h:mm a',
    lastDay: '[Ontem às] h:mm a',
    lastWeek: '[Último] dddd [às] h:mm a',
    sameElse: 'dddd D MMM. YYYY'
  }

  const idiomaID: IdiomasDisponibles = unref(idiomaVisible) || 'es'
  return fecha.calendar(
    null, {
      es: contextoES,
      en: contextoEN,
      pt: contextoPT
    }[idiomaID]
  )
}

export function fechayHoraContextual(fecha: Dayjs) {
  const contextoEN = {
    sameDay: '[Today,] h:mm a',
    nextDay: '[Tomorrow,] h:mm a',
    nextWeek: 'dddd, h:mm a',
    lastDay: '[Yesterday,] h:mm a',
    lastWeek: '[Last] dddd, h:mm a',
    sameElse: 'dddd D MMM. YYYY, h:mm a'
  }
  const contextoES = {
    sameDay: '[Hoy] h:mm a',
    nextDay: '[Mañana,] h:mm a',
    nextWeek: 'dddd, h:mm a',
    lastDay: '[Ayer,] h:mm a',
    lastWeek: '[El pasado] dddd, h:mm a',
    sameElse: 'dddd D MMM. YYYY, h:mm a'
  }

  const contextoPT = {
    sameDay: '[Hoje,] h:mm a',
    nextDay: '[Amanhã,] h:mm a',
    nextWeek: 'dddd, h:mm a',
    lastDay: '[Ontem,] h:mm a',
    lastWeek: '[Último] dddd, h:mm a',
    sameElse: 'dddd D MMM. YYYY, h:mm a'
  }
  const idiomaID: IdiomasDisponibles = unref(idiomaVisible) || 'es'
  return fecha.calendar(
    null,
    {
      es: contextoES,
      en: contextoEN,
      pt: contextoPT
    }[idiomaID]
  )
}

dayjs.fechaContextual = fechaContextual
dayjs.fechayHoraContextual = fechayHoraContextual
dayjs.cambiarIdiomaAlDeRosetta = cambiarIdiomaAlDeRosetta


export { dayjs }

const almacenDeTiempo = {
  ahora: ref(dayjs()),
  minutero: ref(dayjs()),
  hoy: ref(dayjs().startOf('day')),
  timezone: ref(dayjs.tz.guess())
}

export const reloj = computed(() => unref(almacenDeTiempo.ahora))
export const minutero = computed(() => unref(almacenDeTiempo.minutero))
export const hoy = computed(() => unref(almacenDeTiempo.hoy))
export const timezoneCliente = computed(() => unref(almacenDeTiempo.timezone))

function ejecutarActualizacionDeReloj() {
  if (typeof window === 'undefined') {
    return
  }

  window.requestAnimationFrame(() => {
    const ahora = dayjs()
    const min = unref(almacenDeTiempo.minutero)

    almacenDeTiempo.ahora.value = ahora

    if (!almacenDeTiempo.hoy.value.isSame(ahora, 'day'))
      almacenDeTiempo.hoy.value = ahora.startOf('day')

    if (!min.isSame(ahora, 'minute'))
      almacenDeTiempo.minutero.value = ahora.startOf('minute')

    const proxMomento = ahora.add(1, 'seconds').set('millisecond', 0)
    setTimeout(() => {
      actualizarReloj()
    }, proxMomento.diff(almacenDeTiempo.ahora.value))
  })
}

export const actualizarReloj = lodash.throttle(() => {
  if (typeof window === 'undefined') return
  ejecutarActualizacionDeReloj()
}, 1000, { leading: true, trailing: true })


actualizarReloj()
